import type { EChartsOption } from 'echarts';
import type { ChartDataItem, BarChartConfig } from '../types';
import { buildAnimationConfig } from '../shared/animation';
import { buildCategoryAxis, buildValueAxis, buildAxisLabelFormatter, AXIS_LABEL_FONT_SIZE } from '../shared/axis';
import { buildGridConfig } from '../shared/grid';
import { buildLegendConfig } from '../shared/legend';
import { buildTooltipConfig, buildTooltipHeader, buildColorDot, buildTooltipDivider } from '../shared/tooltip';
import { measureTextWidth } from '../shared/measureText';
import { hasOnlyZeroValues } from '../shared/valueAxis';
import { buildBarSeries } from './series';

// echarts axisLabel default margin (gap between the tick text and the axis line).
const AXIS_LABEL_MARGIN = 8;
// Cushion beyond the measured label width — covers ECharts rounding its computed max tick up
// to a "nice" round number that data.max doesn't already reflect (e.g. 162000 -> 200000).
const NAME_GAP_BUFFER = 6;

type TooltipParam = {
  name?: string;
  dataIndex?: number;
  seriesName?: string;
  componentType?: string;
};

const ITEMS_PER_COLUMN = 8;

const buildTooltipFormatter = (
  data: ChartDataItem[],
  orderedFields: string[],
  categoryField: string,
  config: BarChartConfig,
) => {
  const {
    theme,
    tooltipTotalLabel = 'Total',
    absoluteValues = false,
    formatAmount,
    privacyMask,
    customDateText,
    colorOverrides,
    simpleTooltip,
    showTooltipCents = false,
  } = config;

  const tooltipAmountFormat = showTooltipCents ? '0,0.00' : '0,0';

  const fmtAmount = (rawValue: number): string => {
    if (absoluteValues && rawValue < 0) {
      return String(privacyMask(formatAmount(Math.abs(rawValue), 'USD', tooltipAmountFormat)));
    }
    return String(privacyMask(formatAmount(rawValue, 'USD', tooltipAmountFormat)));
  };

  return (params: unknown): string => {
    // trigger: 'axis' gives an array (one entry per series at the nearest category); array
    // order is series-registration order, not cursor proximity, so params[0] isn't
    // necessarily "the hovered one" — take it only for the shared category/dataIndex, and get
    // the actually-hovered series from hoveredFieldNameRef (kept current via mouseover below).
    const p = (Array.isArray(params) ? params[0] : params) as TooltipParam;
    if (!p) return '';

    const { dataIndex } = p;
    // Bolding one row only makes sense when there's something else to contrast it against.
    const hoveredSeriesName = orderedFields.length > 1 ? (config.hoveredFieldNameRef?.current ?? '') : '';
    const dataRow = typeof dataIndex === 'number' ? data[dataIndex] : undefined;
    const axisLabel = p.name ?? (dataRow ? String(dataRow[categoryField] ?? '') : '');
    const category = customDateText && dataRow?.custom != null ? String(dataRow.custom) : axisLabel;

    const colors = colorOverrides ?? theme.colors;

    // Reverse so top-of-stack series appears first; track original index for color lookup
    const allItems = [...orderedFields].reverse().map((field, reversedIdx) => {
      const originalIdx = orderedFields.length - 1 - reversedIdx;
      const fieldVal = dataRow?.[field];
      const rawValue = typeof fieldVal === 'number' ? fieldVal : 0;
      return {
        field,
        rawValue,
        color: colors[originalIdx % colors.length] ?? '',
        isHovered: field === hoveredSeriesName,
      };
    });

    const nonZero = allItems.filter((item) => item.rawValue !== 0);

    const total = orderedFields.reduce((sum, field) => {
      const val = dataRow?.[field];
      return sum + (typeof val === 'number' ? val : 0);
    }, 0);

    const totalLabel = tooltipTotalLabel;
    const totalFormatted = fmtAmount(total);

    // Compact single-value layout — amount + category stacked on the left, a legend
    // dot + label on the right. Skips the multi-series breakdown grid entirely.
    if (simpleTooltip) {
      const dotColor = colors[0] ?? theme.textColor;
      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:20px;">
        <div style="display:flex;flex-direction:column;gap:2px;">
          <span style="font-size:16px;font-weight:600;color:${theme.textColor};">${totalFormatted}</span>
          <span style="font-size:13px;font-weight:400;color:${theme.textColor};">${category}</span>
        </div>
        <div style="display:flex;align-items:center;gap:6px;white-space:nowrap;">
          <span style="width:8px;height:8px;border-radius:50%;background:${dotColor};display:inline-block;"></span>
          <span style="font-size:13px;color:${theme.textSecondaryColor};">${totalLabel}</span>
        </div>
      </div>`;
    }

    const footerHTML =
      `${buildTooltipHeader(
        category,
        theme,
      )}<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;">` +
      `<span style="color:${theme.textColor};font-size:14px;">${totalLabel}</span>` +
      `<span style="color:${theme.textColor};font-size:14px;font-weight:600;">${totalFormatted}</span>` +
      `</div>`;

    if (nonZero.length === 0) return footerHTML;

    // Single-series: the series row (dot + name + amount) is redundant with the footer total,
    // so return just the date header + total rather than the full breakdown grid.
    if (orderedFields.length === 1) return footerHTML;

    const itemsToShow = nonZero;
    const numColumns = Math.max(1, Math.min(3, Math.ceil(itemsToShow.length / ITEMS_PER_COLUMN)));
    const itemsPerColumn = Math.ceil(itemsToShow.length / numColumns);
    const gridCols = numColumns === 1 ? '1fr' : numColumns === 2 ? '1fr 1fr' : '1fr 1fr 1fr';

    let listHTML = `<div style="display:grid;grid-template-columns:${gridCols};gap:12px;max-width:600px;">`;
    for (let col = 0; col < numColumns; col++) {
      listHTML += '<div style="display:flex;flex-direction:column;gap:2px;">';
      const slice = itemsToShow.slice(col * itemsPerColumn, (col + 1) * itemsPerColumn);
      for (const item of slice) {
        const fontWeight = item.isHovered ? '600' : '400';
        const textColor = item.isHovered ? theme.textColor : theme.textSecondaryColor;
        listHTML +=
          `<div style="display:grid;grid-template-columns:12px 1fr auto;gap:6px;align-items:center;">${buildColorDot(
            item.color,
          )}<span style="font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:${textColor};font-weight:${fontWeight};" title="${item.field}">${item.field}</span>` +
          `<span style="font-size:12px;font-weight:${fontWeight};color:${textColor};">${fmtAmount(item.rawValue)}</span>` +
          `</div>`;
      }
      listHTML += '</div>';
    }
    listHTML += '</div>';

    return listHTML + buildTooltipDivider(theme) + footerHTML;
  };
};

export const buildBarChartOption = (
  data: ChartDataItem[],
  orderedFields: string[],
  config: BarChartConfig,
): EChartsOption => {
  const {
    theme,
    withLegend = false,
    animated = true,
    categoryField = 'category',
    colorOverrides,
    xAxisTitle,
    yAxisTitle,
    title,
    showGridLines,
  } = config;

  const categories = data.map((d) => String(d[categoryField] ?? ''));
  const numericValues = data.flatMap((row) =>
    orderedFields.map((field) => row[field]).filter((value): value is number => typeof value === 'number'),
  );
  const currencyMaxMagnitude = numericValues.length ? Math.max(...numericValues.map(Math.abs)) : 0;
  // ECharts otherwise creates fractional ticks for an all-zero series. Those all format as
  // "$0" at this axis's display precision, producing repeated labels in empty dashboard cards.
  const hasZeroOnlyValues = hasOnlyZeroValues(data, orderedFields);

  let yAxisNameGap = 28;
  let yAxisLeft = 16;
  if (yAxisTitle) {
    // Measure the widest label with the same compact formatter the axis renders, so the
    // reserved left gutter matches the actual ($2K-style) tick labels rather than full numbers.
    const sampleLabel = buildAxisLabelFormatter({
      formatAmount: config.formatAmount,
      privacyMask: config.privacyMask,
      useNumberPrefix: true,
      currencyMaxMagnitude,
    })(currencyMaxMagnitude);
    const labelWidth = measureTextWidth(sampleLabel, `${AXIS_LABEL_FONT_SIZE}px ${theme.fontFamily}`);
    // grid.left only needs to fit the tick LABELS — nameGap already independently measures
    // how far the rotated title sits from the axis line (past the labels), so grid.left
    // should NOT also add nameGap's extent on top of itself; that was double-reserving space.
    yAxisNameGap = Math.ceil(labelWidth) + AXIS_LABEL_MARGIN + NAME_GAP_BUFFER;
    yAxisLeft = Math.ceil(labelWidth) + AXIS_LABEL_MARGIN;
  }

  return {
    ...buildAnimationConfig(animated),
    ...(title
      ? {
          title: {
            text: title,
            top: 8,
            left: 'center' as const,
            textStyle: { color: theme.textColor, fontFamily: theme.fontFamily, fontSize: 16, fontWeight: 500 },
          },
        }
      : {}),
    textStyle: { fontFamily: theme.fontFamily },
    color: colorOverrides ?? theme.colors,
    legend: buildLegendConfig(withLegend, theme),
    grid: buildGridConfig({ withLegend, hasTitle: Boolean(title), yAxisLeft }),
    tooltip: {
      ...buildTooltipConfig(theme),
      // 'axis' trigger shows the tooltip anywhere in a category's column, not just when the
      // cursor is exactly over a bar's shape. Which specific series is bolded inside the
      // tooltip is driven separately by hoveredFieldNameRef (see buildTooltipFormatter).
      trigger: 'axis' as const,
      formatter: buildTooltipFormatter(data, orderedFields, categoryField, config),
    },
    xAxis: buildCategoryAxis(categories, theme, xAxisTitle, showGridLines),
    yAxis: buildValueAxis({
      theme,
      formatAmount: config.formatAmount,
      privacyMask: config.privacyMask,
      title: yAxisTitle,
      showGridLines,
      nameGap: yAxisNameGap,
      // Compact axis labels ($2K / -$2K) to match the AMCharts production chart, whose
      // chart-wide numberFormat abbreviates ('-$#,###.00s').
      useNumberPrefix: true,
      currencyMaxMagnitude,
      ...(hasZeroOnlyValues && { min: 0, max: 1, splitNumber: 1 }),
    }),
    series: buildBarSeries(data, orderedFields, categoryField, config),
  };
};
